import { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "@/shared/i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Skeleton } from "@/shared/ui/skeleton";
import MultipleSelector, { Option } from "@/shared/ui/multiple-selector";
import { Input } from "@/shared/ui/input";
import { DateRangePicker } from "@/shared/widgets/DateRangePicker";
import { useDefaultDatePresets } from "@/shared/widgets/DateRangePicker/presets";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import CreatorService from "@/features/creators/services/creatorService";
import type { Creator } from "@/features/creators/services/creatorService";
import {
  ReceivableCurrency,
  ReceivableStatus,
} from "@/features/receivables/utils/types";

const STATUS_OPTIONS: ReceivableStatus[] = [
  ReceivableStatus.WAITING,
  ReceivableStatus.CALCULATING,
  ReceivableStatus.CALCULATED,
  ReceivableStatus.RECEIVED,
  ReceivableStatus.CONCLUDED,
  ReceivableStatus.FAIL,
];

interface Props {
  selectedCreators: Option[];
  onCreatorsChange: (next: Option[]) => void;
  creatorName: string;
  onCreatorNameChange: (next: string) => void;
  status: ReceivableStatus | "all";
  onStatusChange: (next: ReceivableStatus | "all") => void;
  currency: ReceivableCurrency;
  onCurrencyChange: (next: ReceivableCurrency) => void;
  dateRange: DateRange | undefined;
  onDateRangeChange: (next: DateRange | undefined) => void;
}

export function FinancialFilters({
  selectedCreators,
  onCreatorsChange,
  creatorName,
  onCreatorNameChange,
  status,
  onStatusChange,
  currency,
  onCurrencyChange,
  dateRange,
  onDateRangeChange,
}: Props) {
  const { t } = useTranslation("admin-financial-overview");

  const { data: creators = [], isLoading: isLoadingCreators } = useCustomQuery({
    serviceInstance: CreatorService.Instance,
    selectAction: (service) => service.getCreators,
    parameter: undefined,
  }) as { data: Creator[]; isLoading: boolean };

  const creatorOptions = useMemo<Option[]>(
    () => creators.map((c) => ({ value: c.id, label: c.name })),
    [creators]
  );

  const datePresets = useDefaultDatePresets();

  // Hidrata os labels dos chips quando os creators terminam de carregar.
  // Derivado via useMemo (não useEffect → state) pra evitar render-set-rerender.
  const hydratedSelected = useMemo<Option[]>(() => {
    if (!creators.length || !selectedCreators.length) return selectedCreators;
    return selectedCreators.map((sel) => {
      if (sel.label !== sel.value) return sel;
      const found = creators.find((c) => c.id === sel.value);
      return found ? { value: found.id, label: found.name } : sel;
    });
  }, [creators, selectedCreators]);

  return (
    <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center">
      <div className="w-full md:w-[320px]">
        {isLoadingCreators ? (
          <Skeleton className="h-10 w-full bg-[#212121] border border-white/10" />
        ) : (
          <MultipleSelector
            value={hydratedSelected}
            onChange={onCreatorsChange}
            defaultOptions={creatorOptions}
            placeholder={t("filterCreatorsPlaceholder")}
            hidePlaceholderWhenSelected
            className="flex min-h-10 border-white/10 bg-[#212121]"
            emptyIndicator={
              <span className="text-white">{t("filterCreatorsEmpty")}</span>
            }
            badgeClassName="rounded-sm text-normal text-gray-300 border border-gray-100 bg-gray-300/20 hover:bg-gray-600/50"
          />
        )}
      </div>

      <div className="w-full md:w-[240px]">
        <Input
          value={creatorName}
          onChange={(e) => onCreatorNameChange(e.target.value)}
          placeholder={t("filterCreatorNamePlaceholder")}
          className="h-10 border-white/10 bg-[#212121]"
        />
      </div>

      <Select
        value={status}
        onValueChange={(v) => onStatusChange(v as ReceivableStatus | "all")}
      >
        <SelectTrigger className="w-full md:w-[180px]">
          <SelectValue placeholder={t("filterStatusPlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("filterStatusAll")}</SelectItem>
          {STATUS_OPTIONS.map((s) => (
            <SelectItem key={s} value={s}>
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={currency}
        onValueChange={(v) => onCurrencyChange(v as ReceivableCurrency)}
      >
        <SelectTrigger className="w-full md:w-[120px]">
          <SelectValue placeholder={t("filterCurrencyPlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ReceivableCurrency.BRL}>BRL</SelectItem>
          <SelectItem value={ReceivableCurrency.USD}>USD</SelectItem>
        </SelectContent>
      </Select>

      <div className="w-full md:w-[300px]">
        <DateRangePicker
          dateRange={dateRange}
          setDateRange={onDateRangeChange}
          placeholder={t("filterPeriodPlaceholder")}
          presets={datePresets}
        />
      </div>
    </div>
  );
}
